// The anonymous waitlist form (ADR-0023).
//
// This component never touches the database. The `waitlist` schema is not in
// PostgREST's exposed set and holds no grants for `anon`, so there is no route
// from a browser to those tables at all — the form posts to the Cloudflare
// Worker, which holds the only credential that can write.
//
// The submitted state is shown whatever came back, because the endpoint answers
// identically for a new address, a duplicate and one that is already admitted.
// Branching this UI on the response would rebuild the enumeration oracle the
// endpoint was built to remove.

import { useEffect, useRef, useState } from "react";
import { joinWaitlist } from "../lib/access";
import { TURNSTILE_SITE_KEY, loadTurnstile } from "../lib/turnstile";
import { Spinner } from "./Spinner";

export function WaitlistForm({ source }: { source?: string }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const widget = useRef<HTMLDivElement>(null);

  const widgetId = useRef<string | null>(null);

  /**
   * The action is pinned here and checked again in the Worker against
   * siteverify, so a token minted for the contact widget is refused here.
   *
   * THIS USED TO READ `window.turnstile` ONCE, ON MOUNT. Nothing in the app
   * ever loaded that script, so the value was always `undefined`, this widget
   * was always an empty div — which is what the `.cf-turnstile:empty` rule in
   * styles.css was hiding — and every submission went up with no token at all.
   * Because this form answers every outcome identically by design, the refusal
   * was invisible from the browser. See the header of lib/turnstile.ts.
   */
  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((api) => {
      if (cancelled || !api || !widget.current) return;
      if (widget.current.dataset.rendered) return;
      widgetId.current = api.render(widget.current, {
        sitekey: TURNSTILE_SITE_KEY!,
        action: "waitlist",
      });
      widget.current.dataset.rendered = "true";
    });
    return () => { cancelled = true; };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    const params = new URLSearchParams(window.location.search);
    try {
      await joinWaitlist({
        email,
        token: (window as any).turnstile?.getResponse(widgetId.current ?? undefined),
        source: source ?? params.get("utm_source"),
        campaign: params.get("utm_campaign"),
      });
    } catch {
      // Even a network failure lands on the same screen. The one thing worse
      // than an unclear confirmation is a form that tells a stranger which
      // addresses we already hold.
    } finally {
      setBusy(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <div className="card">
        <p>Thanks — check your email and confirm the address.</p>
        <p className="muted small">
          We only use it to tell you when a place opens up. Nothing else.
        </p>
      </div>
    );
  }

  return (
    <form className="card waitlist-form" onSubmit={submit}>
      <label htmlFor="waitlist-email">Email address</label>
      <input
        id="waitlist-email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      {/* Hidden from people, irresistible to bots. A filled one is answered
          exactly like a success, so nothing is learned by filling it. */}
      <input
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: "none" }}
        onChange={() => { /* never read; the Worker sends "" regardless */ }}
      />

      <div ref={widget} className="cf-turnstile" />

      <button className="primary" type="submit" disabled={busy}>
        {busy ? <Spinner label="Joining the waitlist" /> : "Join the waitlist"}
      </button>

      <p className="muted small">
        We use your address only to tell you when a place opens up.{" "}
        <a href="/privacy">Privacy</a>
      </p>
    </form>
  );
}
